import { useCallback, useRef } from 'react';
import { trackEventMixpanel } from 'utils/mixpanel';

import StaticWhitespaceCard from 'components/organism/WhitespaceAnalysis/StaticWhitespaceCard';
import type { StaticWhitespaceCardProps } from 'components/organism/WhitespaceAnalysis/StaticWhitespaceCard';
import { PPA_WHITESPACE_CELL_HOVER, PPA_WHITESPACE_ENTITY_LIST_COLLAPSED, PPA_WHITESPACE_ENTITY_MORE_CLICKED } from 'utils/mixpanelEventNames';
import { buildWhitespaceCellId, getWhitespaceCellHoverProps, WHITESPACE_CELL_HOVER_TRACK_MIN_MS } from './whitespaceMixpanel.helpers';

type WhitespaceGridCell = Partial<StaticWhitespaceCardProps>;

const getYAxisAttributeProps = (isYAxisAttribute: boolean, yAxis: string, rowLabel: string) => ({
    yAxisAttributeLabel: isYAxisAttribute ? yAxis : undefined,
    yAxisAttributeValue: isYAxisAttribute ? rowLabel : undefined,
});

type UseWhitespaceCellRendererParams = {
    xAxis: string;
    yAxis: string;
    crossSection: string;
    showWhitespaces: boolean;
    selectedYAxisKpiId: string;
    isYAxisAttribute?: boolean;
    getCellPaginationData: (params: {
        bucketLabel: string;
        columnHeader: string;
        defaultItems: StaticWhitespaceCardProps['items'];
        defaultMoreCount: number;
    }) => {
        items: StaticWhitespaceCardProps['items'];
        moreCount: number;
        isLoading: boolean;
        showCollapse: boolean;
    };
    collapseCellItems: (params: { bucketLabel: string; columnHeader: string }) => void;
    loadMoreForCell: (params: {
        bucketLabel: string;
        columnHeader: string;
        defaultItems: StaticWhitespaceCardProps['items'];
        defaultMoreCount: number;
    }) => Promise<{ previousEntityCount: number; newEntityCount: number } | undefined>;
};

const useWhitespaceCellRenderer = ({
    xAxis,
    yAxis,
    crossSection,
    showWhitespaces,
    selectedYAxisKpiId,
    isYAxisAttribute = false,
    getCellPaginationData,
    collapseCellItems,
    loadMoreForCell,
}: UseWhitespaceCellRendererParams) => {
    const cellHoverStartRef = useRef<{
        key: string;
        startedAt: number;
        rowLabel: string;
        columnHeader: string;
        crossSectionValue: string;
    } | null>(null);

    const trackCellHoverIfEligible = useCallback(
        (hover: NonNullable<typeof cellHoverStartRef.current>, endedAt = Date.now()) => {
            const hoverDurationMs = endedAt - hover.startedAt;
            if (hoverDurationMs <= WHITESPACE_CELL_HOVER_TRACK_MIN_MS) return;
            trackEventMixpanel(
                PPA_WHITESPACE_CELL_HOVER,
                getWhitespaceCellHoverProps({
                    xAxis,
                    yAxis,
                    xAxisValue: hover.columnHeader,
                    yAxisValue: hover.rowLabel,
                    axisBucketValue: hover.rowLabel,
                    crossSection,
                    crossSectionValue: hover.crossSectionValue,
                    hoverDurationMs,
                }),
            );
        },
        [crossSection, xAxis, yAxis],
    );

    const handleCellHover = useCallback(
        (cellKey: string, rowLabel: string, columnHeader: string, crossSectionValue: string) => {
            const hoverKey = `${cellKey}:${crossSectionValue}`;
            const activeHover = cellHoverStartRef.current;
            if (activeHover?.key === hoverKey) return;
            if (activeHover) {
                trackCellHoverIfEligible(activeHover);
            }
            cellHoverStartRef.current = {
                key: hoverKey,
                startedAt: Date.now(),
                rowLabel,
                columnHeader,
                crossSectionValue,
            };
        },
        [trackCellHoverIfEligible],
    );

    const handleCellHoverEnd = useCallback(() => {
        const activeHover = cellHoverStartRef.current;
        cellHoverStartRef.current = null;
        if (!activeHover) return;
        trackCellHoverIfEligible(activeHover);
    }, [trackCellHoverIfEligible]);

    const handleCollapseClick = useCallback(
        (cellId: string, rowLabel: string, columnHeader: string, previousEntityCount: number) => {
            collapseCellItems({ bucketLabel: rowLabel, columnHeader });
            trackEventMixpanel(PPA_WHITESPACE_ENTITY_LIST_COLLAPSED, {
                cell_id: cellId,
                previous_entity_count: previousEntityCount,
                cross_section: crossSection,
            });
        },
        [collapseCellItems, crossSection],
    );

    const handleMoreClick = useCallback(
        async (cellId: string, cell: WhitespaceGridCell, rowLabel: string, columnHeader: string) => {
            const result = await loadMoreForCell({
                bucketLabel: rowLabel,
                columnHeader,
                defaultItems: cell.items ?? [],
                defaultMoreCount: cell.moreCount ?? 0,
            });
            if (!result) return;
            trackEventMixpanel(PPA_WHITESPACE_ENTITY_MORE_CLICKED, {
                cell_id: cellId,
                previous_entity_count: result.previousEntityCount,
                new_entity_count: result.newEntityCount,
                cross_section: crossSection,
            });
        },
        [crossSection, loadMoreForCell],
    );

    const renderWhitespaceCell = useCallback(
        (cell: WhitespaceGridCell, rowLabel: string, columnHeader: string) => {
            const paginationData = getCellPaginationData({
                bucketLabel: rowLabel,
                columnHeader,
                defaultItems: cell.items ?? [],
                defaultMoreCount: cell.moreCount ?? 0,
            });
            const cellId = buildWhitespaceCellId(columnHeader, yAxis, rowLabel);
            const { yAxisAttributeLabel, yAxisAttributeValue } = getYAxisAttributeProps(
                isYAxisAttribute,
                yAxis,
                rowLabel,
            );

            return (
                <StaticWhitespaceCard
                    valueSales={cell.valueSales ?? ''}
                    valuePrefix={cell.valuePrefix ?? ''}
                    valueSuffix={cell.valueSuffix ?? ''}
                    trendValue={typeof cell.trendValue === 'number' ? cell.trendValue : null}
                    trendDirection={cell.trendDirection ?? 'neutral'}
                    avgValueSales={cell.avgValueSales ?? ''}
                    avgPrefix={cell.avgPrefix ?? ''}
                    avgSuffix={cell.avgSuffix ?? ''}
                    ownShareKpi={cell.ownShareKpi ?? null}
                    crossSectionShareLabel={cell.crossSectionShareLabel ?? ''}
                    crossSectionLabel={crossSection}
                    items={paginationData.items}
                    moreCount={paginationData.moreCount}
                    heatmapColor={cell.heatmapColor ?? 'transparent'}
                    isWhitespace={Boolean(cell.isWhitespace)}
                    showWhitespaces={showWhitespaces}
                    isMoreLoading={paginationData.isLoading}
                    showCollapseButton={paginationData.showCollapse}
                    selectedYAxisKpiId={selectedYAxisKpiId}
                    yAxisAttributeLabel={yAxisAttributeLabel}
                    yAxisAttributeValue={yAxisAttributeValue}
                    onCellHover={(crossSectionValue) =>
                        handleCellHover(cellId, rowLabel, columnHeader, crossSectionValue)
                    }
                    onCellHoverEnd={handleCellHoverEnd}
                    onCollapseClick={() =>
                        handleCollapseClick(
                            cellId,
                            rowLabel,
                            columnHeader,
                            paginationData.items.length,
                        )
                    }
                    onMoreClick={() =>
                        handleMoreClick(
                            cellId,
                            cell,
                            rowLabel,
                            columnHeader,
                        )
                    }
                />
            );
        },
        [
            crossSection,
            getCellPaginationData,
            handleCellHover,
            handleCellHoverEnd,
            handleCollapseClick,
            handleMoreClick,
            isYAxisAttribute,
            selectedYAxisKpiId,
            showWhitespaces,
            yAxis,
        ],
    );

    return renderWhitespaceCell;
};

export default useWhitespaceCellRenderer;
